import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, ShieldCheck, Loader2, ArrowRight, Clock, AlertTriangle, Ban, RefreshCw, Lock, Sparkles, Lightbulb, Camera, Sun } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { createIdentityVerificationSession } from "@/lib/kyc.functions";

export const Route = createFileRoute("/_authenticated/verify")({
  head: () => ({
    meta: [
      { title: "Identity verification — Lumvio" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: VerifyPage,
});

function VerifyPage() {
  const { t } = useTranslation();
  const { user, profile, loading, refreshProfile } = useAuth();

  // Refresh profile when returning from Stripe (webhook may have updated status).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get("stripe_check")) return;
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts += 1;
      await refreshProfile();
      if (attempts >= 6) clearInterval(iv);
    }, 2000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startSession = useServerFn(createIdentityVerificationSession);
  const startMut = useMutation({
    mutationFn: () => startSession({}),
    onSuccess: (res) => {
      if (res?.url) {
        import("@/lib/posthog").then(({ capturePostHog, PHEvents }) =>
          capturePostHog(PHEvents.KycStarted),
        ).catch(() => {});
        window.location.href = res.url;
      } else {
        toast.error("Could not start verification.");
      }
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to start verification"),
  });

  if (loading) {
    return (
      <div className="mx-auto flex max-w-lg items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" />;
  if (profile?.role !== "freelancer") return <Navigate to="/dashboard" />;

  const status = profile?.kyc_status ?? "not_started";
  const reason = profile?.kyc_last_reason ?? null;
  const sessionId = profile?.stripe_verification_session_id ?? null;

  // Derive a granular display state that mirrors Stripe's session states.
  // The DB collapses to verified/rejected/pending, so we split "rejected"
  // into "canceled" vs "requires_input" using the stored reason.
  type DisplayState = "verified" | "processing" | "requires_input" | "canceled";
  let displayState: DisplayState | null = null;
  if (status === "verified") displayState = "verified";
  else if (status === "pending") displayState = "processing";
  else if (status === "rejected") {
    displayState = reason?.toLowerCase().includes("cancel") ? "canceled" : "requires_input";
  }

  if (displayState) {
    return (
      <div className="mx-auto max-w-lg">
        <KycStatusSection
          state={displayState}
          reason={reason}
          sessionId={sessionId}
          starting={startMut.isPending}
          onRetry={async () => {
            if (displayState !== "verified") {
              const { error } = await supabase.rpc("reset_kyc_for_retry");
              if (error) {
                toast.error(error.message);
                return;
              }
              await refreshProfile();
            }
            startMut.mutate();
          }}
          onRefresh={() => refreshProfile()}
        />
      </div>
    );
  }


  // not_started — Premium Trust & Safety card
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card p-8 shadow-2xl"
      >
        {/* Ambient glow */}
        <div className="pointer-events-none absolute -end-16 -top-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -start-10 h-48 w-48 rounded-full bg-emerald-500/10 blur-3xl" />

        <div className="relative space-y-6">
          {/* Bank-Grade badge */}
          <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-500 shadow-[0_0_20px_-5px] shadow-emerald-500/40">
            <ShieldCheck className="h-3.5 w-3.5" />
            {t("verify.bankGrade", { defaultValue: "Bank-Grade Security" })}
          </div>

          {/* Lock hero */}
          <div className="flex items-start gap-5">
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.1 }}
              className="relative flex h-16 w-16 flex-none items-center justify-center rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/20 to-primary/5 shadow-lg shadow-primary/20"
            >
              <Lock className="h-8 w-8 text-primary" strokeWidth={1.8} />
              <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10" />
            </motion.div>
            <div className="min-w-0 pt-1">
              <h1 className="text-2xl font-semibold tracking-tight">
                {t("verify.trustTitle", { defaultValue: "Trust & Safety" })}
              </h1>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {t("verify.trustBody", {
                  defaultValue:
                    "Verify your identity via Stripe to unlock premium client portals, payment protection protection, and direct payouts.",
                })}
              </p>
            </div>
          </div>

          {/* Feature list */}
          <ul className="grid gap-3 sm:grid-cols-3">
            {[
              {
                icon: ShieldCheck,
                title: t("verify.point1.title", { defaultValue: "Bank-grade" }),
                body: t("verify.point1.short", { defaultValue: "Powered by Stripe Identity" }),
              },
              {
                icon: CheckCircle2,
                title: t("verify.point2.title", { defaultValue: "ID + selfie" }),
                body: t("verify.point2.short", { defaultValue: "Passport, ID, or license" }),
              },
              {
                icon: Clock,
                title: t("verify.point3.title", { defaultValue: "~1 minute" }),
                body: t("verify.point3.short", { defaultValue: "Instant automatic update" }),
              },
            ].map((f, i) => {
              const Ico = f.icon;
              return (
                <motion.li
                  key={f.title}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.07 }}
                  className="rounded-xl border border-border/60 bg-background/40 p-3 backdrop-blur-sm"
                >
                  <Ico className="mb-1.5 h-4 w-4 text-primary" />
                  <div className="text-xs font-semibold">{f.title}</div>
                  <div className="text-[11px] text-muted-foreground">{f.body}</div>
                </motion.li>
              );
            })}
          </ul>
        </div>
      </motion.div>

      {/* Sticky CTA */}
      <div className="sticky bottom-4 rounded-2xl border border-primary/20 bg-card/95 p-4 shadow-2xl shadow-primary/10 backdrop-blur-md">
        <Button
          size="lg"
          className="group relative w-full overflow-hidden bg-gradient-to-r from-primary to-primary/80 shadow-lg shadow-primary/30 transition-all hover:shadow-primary/50"
          disabled={startMut.isPending}
          onClick={() => startMut.mutate()}
        >
          {startMut.isPending ? (
            <>
              <Loader2 className="me-2 h-4 w-4 animate-spin" />
              {t("verify.securing", { defaultValue: "Securing connection to Stripe Identity..." })}
            </>
          ) : (
            <>
              <Sparkles className="me-2 h-4 w-4" />
              {t("verify.startFree", { defaultValue: "Start Free — Verify to Unlock" })}
              <ArrowRight className="ms-1 h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" />
            </>
          )}
        </Button>
        <p className="mt-2 text-center text-[11px] text-muted-foreground">
          {t("verify.redirectNote", {
            defaultValue: "You'll be redirected to Stripe's secure environment.",
          })}
        </p>
      </div>
    </div>
  );
}


type KycDisplayState = "verified" | "processing" | "requires_input" | "canceled";

function KycStatusSection({
  state,
  reason,
  sessionId,
  starting,
  onRetry,
  onRefresh,
}: {
  state: KycDisplayState;
  reason: string | null;
  sessionId: string | null;
  starting: boolean;
  onRetry: () => void;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();

  const meta = {
    verified: {
      icon: CheckCircle2,
      tint: "text-emerald-500",
      bg: "bg-emerald-500/10",
      ring: "ring-emerald-500/20",
      label: t("verify.status.verified.label", { defaultValue: "Verified" }),
      title: t("verify.status.verified.title", { defaultValue: "Your identity is verified" }),
      body: t("verify.status.verified.body", {
        defaultValue: "You have full access to Lumvio. Nothing else to do here.",
      }),
    },
    processing: {
      icon: Loader2,
      tint: "text-amber-500",
      bg: "bg-amber-500/10",
      ring: "ring-amber-500/20",
      label: t("verify.status.processing.label", { defaultValue: "Processing" }),
      title: t("verify.status.processing.title", { defaultValue: "Verification in progress" }),
      body: t("verify.status.processing.body", {
        defaultValue:
          "Stripe is reviewing your submission. This usually completes in under a minute. This page updates automatically.",
      }),
    },
    requires_input: {
      icon: AlertTriangle,
      tint: "text-destructive",
      bg: "bg-destructive/10",
      ring: "ring-destructive/20",
      label: t("verify.status.requiresInput.label", { defaultValue: "Action required" }),
      title: t("verify.status.requiresInput.title", { defaultValue: "We need another attempt" }),
      body: t("verify.status.requiresInput.body", {
        defaultValue:
          "Stripe couldn't verify your identity from the last submission. Please try again with a clearer photo of your ID and a well-lit selfie.",
      }),
    },
    canceled: {
      icon: Ban,
      tint: "text-muted-foreground",
      bg: "bg-muted",
      ring: "ring-border",
      label: t("verify.status.canceled.label", { defaultValue: "Canceled" }),
      title: t("verify.status.canceled.title", { defaultValue: "Verification canceled" }),
      body: t("verify.status.canceled.body", {
        defaultValue: "Your last verification session was canceled. You can start a new one whenever you're ready.",
      }),
    },
  }[state];

  const Icon = meta.icon;
  const showRetry = state !== "verified";

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border bg-card p-8 shadow-2xl ${
        state === "verified" ? "border-emerald-500/30 shadow-emerald-500/10" : ""
      }`}
    >
      {/* Verified: ambient glow + confetti burst */}
      {state === "verified" && (
        <>
          <div className="pointer-events-none absolute -top-24 start-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {Array.from({ length: 14 }).map((_, i) => {
              const angle = (i / 14) * Math.PI * 2;
              const distance = 120 + (i % 3) * 30;
              const colors = ["bg-emerald-400", "bg-primary", "bg-amber-300", "bg-emerald-300"];
              return (
                <motion.span
                  key={i}
                  initial={{ x: 0, y: 0, opacity: 1, scale: 0.6 }}
                  animate={{
                    x: Math.cos(angle) * distance,
                    y: Math.sin(angle) * distance,
                    opacity: 0,
                    scale: 1,
                  }}
                  transition={{ duration: 1.4, ease: "easeOut", delay: 0.15 }}
                  className={`absolute start-1/2 top-14 h-1.5 w-1.5 rounded-full ${colors[i % colors.length]}`}
                />
              );
            })}
          </div>
        </>
      )}

      <div className="relative flex items-center gap-4">
        <motion.div
          initial={{ scale: 0.6, opacity: 0, rotate: state === "verified" ? -20 : 0 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 15 }}
          className={`relative flex h-16 w-16 flex-none items-center justify-center rounded-full ring-4 ${meta.bg} ${meta.ring} ${
            state === "verified" ? "shadow-[0_0_40px_-5px] shadow-emerald-500/60" : ""
          }`}
        >
          <Icon className={`h-8 w-8 ${meta.tint} ${state === "processing" ? "animate-spin" : ""}`} />
          {state === "verified" && (
            <motion.span
              initial={{ scale: 0.8, opacity: 0.7 }}
              animate={{ scale: 1.6, opacity: 0 }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
              className="absolute inset-0 rounded-full ring-2 ring-emerald-400/60"
            />
          )}
        </motion.div>
        <div className="min-w-0">
          <div
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${meta.bg} ${meta.tint} ${
              state === "verified" ? "shadow-[0_0_16px_-2px] shadow-emerald-500/50" : ""
            }`}
          >
            {state === "verified" && <CheckCircle2 className="h-3 w-3" />}
            {meta.label}
          </div>
          <h1 className="mt-1 text-xl font-semibold tracking-tight">{meta.title}</h1>
        </div>
      </div>

      <p className="relative mt-5 text-sm text-muted-foreground">{meta.body}</p>

      {state === "verified" && (
        <div className="relative mt-4 flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-500">
          <ShieldCheck className="h-4 w-4 flex-none" />
          <span>
            {t("verify.status.verified.confidence", {
              defaultValue: "100% confidence · ID VERIFIED",
            })}
          </span>
        </div>
      )}

      {state === "requires_input" && (
        <div className="relative mt-4 space-y-2">
          {reason && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
              <span className="font-semibold">
                {t("verify.status.reasonLabel", { defaultValue: "Reason from Stripe:" })}
              </span>{" "}
              <span className="opacity-90">{reason}</span>
            </div>
          )}
          <div className="rounded-lg border border-border/60 bg-muted/40 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold">
              <Lightbulb className="h-3.5 w-3.5 text-amber-400" />
              {t("verify.tips.title", { defaultValue: "Tips for a successful retry" })}
            </div>
            <ul className="space-y-1.5 text-[11px] text-muted-foreground">
              <li className="flex items-start gap-2">
                <Sun className="mt-0.5 h-3 w-3 flex-none text-amber-400" />
                {t("verify.tips.light", { defaultValue: "Use bright, even lighting — avoid shadows and glare." })}
              </li>
              <li className="flex items-start gap-2">
                <Camera className="mt-0.5 h-3 w-3 flex-none text-primary" />
                {t("verify.tips.focus", { defaultValue: "Hold your phone steady — every corner of the ID must be sharp." })}
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-3 w-3 flex-none text-emerald-500" />
                {t("verify.tips.match", { defaultValue: "Selfie should match the name on your Lumvio profile." })}
              </li>
            </ul>
          </div>
        </div>
      )}

      {sessionId && (
        <div className="relative mt-4 text-[11px] text-muted-foreground/70">
          {t("verify.status.sessionRef", { defaultValue: "Session ref" })}:{" "}
          <code className="font-mono">{sessionId.slice(0, 18)}…</code>
        </div>
      )}

      <div className="relative mt-6 flex flex-col gap-2 sm:flex-row">
        {state === "verified" ? (
          <Button
            asChild
            className="flex-1 bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 hover:bg-emerald-500/90"
          >
            <Link to="/dashboard">
              <Sparkles className="me-2 h-4 w-4" />
              {t("common.goToDashboard", { defaultValue: "Go to dashboard" })}
            </Link>
          </Button>
        ) : (
          <>
            {state === "processing" ? (
              <Button variant="outline" className="flex-1" onClick={onRefresh}>
                <RefreshCw className="me-2 h-4 w-4" />
                {t("common.refresh", { defaultValue: "Refresh" })}
              </Button>
            ) : (
              <Button asChild variant="outline" className="flex-1">
                <Link to="/dashboard">{t("common.backToDashboard", { defaultValue: "Back to dashboard" })}</Link>
              </Button>
            )}
            {showRetry && (
              <Button className="flex-1" onClick={onRetry} disabled={starting}>
                {starting ? (
                  <>
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    {t("verify.securing", { defaultValue: "Securing connection to Stripe Identity..." })}
                  </>
                ) : state === "processing" ? (
                  t("verify.pending.restart", { defaultValue: "Start over" })
                ) : (
                  t("common.retry", { defaultValue: "Retry Verification" })
                )}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
